import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LargeTitle, Surface, Pill, AButton, Skeleton, EmptyState, Segmented } from "@/components/ios";
import { Mail, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Notices() {
  const [notices, setNotices] = useState(null);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", report_type: "routine", entry_date: "", entry_time: "10:00am", delivery_method: "email" });
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/notices").then(({ data }) => setNotices(data));
  useEffect(() => { load(); api.get("/properties").then(({ data }) => { setProps(data); setForm((f) => ({ ...f, property_id: data[0]?.id || "" })); }); }, []);

  const send = async () => {
    if (!form.entry_date) { toast.error("Pick an entry date"); return; }
    try {
      const { data } = await api.post("/notices", form);
      if (data.duplicate) toast("Notice already sent (idempotent)");
      else toast.success(data.notice.compliant ? "Notice sent · compliant" : "Notice sent · SHORT NOTICE");
      setPreview(data.notice); setOpen(false); load();
    } catch (e) { toast.error("Could not send notice"); }
  };

  return (
    <div>
      <LargeTitle subtitle="Legally valid entry notices with delivery tracking" right={<AButton testid="new-notice-btn" onClick={() => setOpen(true)}>New notice</AButton>}>Notices</LargeTitle>

      {!notices ? <div className="space-y-3">{[0,1].map(i => <Skeleton key={i} className="h-20" />)}</div>
        : notices.length === 0 ? <Surface><EmptyState icon={Mail} title="No notices sent" subtitle="Generate a state-compliant entry notice for a tenant." action={<AButton onClick={() => setOpen(true)}>New notice</AButton>} /></Surface>
        : <div className="space-y-3">
          {notices.map((n) => (
            <Surface key={n.id} className="p-4 cursor-pointer" onClick={() => setPreview(n)} data-testid={`notice-${n.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="font-medium truncate" style={{ color: "var(--text)" }}>{n.property?.address}</div>
                  <div className="text-sm text-secondary capitalize">{n.report_type} · {new Date(n.entry_date).toLocaleDateString("en-AU")} · via {n.delivery_method}</div></div>
                <div className="flex items-center gap-2 shrink-0">
                  <Pill tone={n.compliant ? "success" : "danger"}>{n.compliant ? <><Check size={10} /> {n.notice_days}d notice</> : <><AlertTriangle size={10} /> short notice</>}</Pill>
                  <Pill tone="accent">{n.status}</Pill>
                </div>
              </div>
            </Surface>
          ))}
        </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <DialogHeader><DialogTitle style={{ color: "var(--text)" }}>New entry notice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
              <SelectTrigger data-testid="notice-property" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }}><SelectValue placeholder="Property" /></SelectTrigger>
              <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.address}, {p.state}</SelectItem>)}</SelectContent>
            </Select>
            <Segmented value={form.report_type} onChange={(v) => setForm({ ...form, report_type: v })} options={[{ value: "entry", label: "Entry" }, { value: "routine", label: "Routine" }, { value: "exit", label: "Exit" }]} />
            <input type="date" data-testid="notice-date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
              className="w-full text-sm rounded-lg px-3 py-2.5 outline-none border" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} />
            <Select value={form.delivery_method} onValueChange={(v) => setForm({ ...form, delivery_method: v })}>
              <SelectTrigger style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }}><SelectValue /></SelectTrigger>
              <SelectContent>{["email", "sms", "post", "in_person"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <AButton testid="send-notice" className="w-full" onClick={send}>Generate & send</AButton>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <DialogHeader><DialogTitle style={{ color: "var(--text)" }}>Entry notice</DialogTitle></DialogHeader>
          <pre className="whitespace-pre-wrap text-sm leading-relaxed p-4 rounded-xl" style={{ background: "var(--surface-2)", color: "var(--text)", fontFamily: "inherit" }}>{preview?.body}</pre>
          <div className="text-xs text-secondary">Delivered {preview && new Date(preview.created_at).toLocaleString("en-AU")} · stored as evidence · idempotency key {preview?.idempotency_key?.slice(0, 10)}…</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
